import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface LinkPreviewData {
  title?: string;
  description?: string;
  image?: string;
  isOgImage?: boolean;
  url: string;
}

// Cache to avoid refetching the same URLs
const previewCache = new Map<string, LinkPreviewData>();

export function useLinkPreview(url: string | undefined) {
  const [preview, setPreview] = useState<LinkPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!url) {
      setPreview(null);
      setLoading(false);
      setError(null);
      return;
    }

    // Normalize URL - add https:// if no protocol specified
    const normalizedUrl = url.match(/^[a-zA-Z]+:\/\//) ? url : `https://${url}`;

    // Check cache first
    const cached = previewCache.get(normalizedUrl);
    if (cached) {
      setPreview(cached);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchPreview = async () => {
      setLoading(true);
      setError(null);

      try {
        // Use our Edge Function to fetch link preview (avoids CORS issues)
        const { data, error: fnError } = await supabase.functions.invoke<LinkPreviewData>(
          "fetch-link-preview",
          { body: { url: normalizedUrl } }
        );

        if (cancelled) return;

        if (fnError) {
          throw new Error(fnError.message);
        }

        if (!data) {
          throw new Error("No data returned");
        }

        const previewData: LinkPreviewData = {
          title: data.title,
          description: data.description,
          image: data.image,
          isOgImage: data.isOgImage,
          url: data.url || normalizedUrl,
        };

        // Cache the result
        previewCache.set(normalizedUrl, previewData);

        if (!cancelled) {
          setPreview(previewData);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err as Error);
          setLoading(false);

          // Still cache a basic result to avoid refetching
          const basicPreview: LinkPreviewData = { url: normalizedUrl };
          previewCache.set(normalizedUrl, basicPreview);
          setPreview(basicPreview);
        }
      }
    };

    fetchPreview();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { preview, loading, error };
}
